import { useEffect, useRef, useState } from 'react';
import { Copy, Check, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { QRCodeCanvas } from '@/components/ui/qrcode';
import { planPriceUsd, type BillingPlan } from '@/hooks/useSubscription';
import { useLiquidityData } from '@/hooks/useLiquidityData';

// BCH Radar receiving address (non-custodial, controlled by the operator).
const BCH_ADDRESS = 'bitcoincash:qrfe5r4afrvfyzcldap2fcvetusqz2u0ky8h0y9292';
const POLL_INTERVAL_MS = 15000;
// How old a price may be before we refuse to quote a payment amount. A user
// must NEVER be charged an amount derived from a stale or fabricated price.
const PRICE_MAX_AGE_MS = 10 * 60 * 1000; // 10 minutes
// A quote older than this is considered stale. The amount stays frozen (so any
// in-flight payment is safe), but we surface an explicit "refresh price" option
// so a user who hasn't paid yet isn't working from a drifted price.
const QUOTE_MAX_AGE_MS = 10 * 60 * 1000; // 10 minutes

const CORS = (url: string) =>
  `https://proxy.shakespeare.diy/?url=${encodeURIComponent(url)}`;

// Reject a rates response whose own timestamp is older than this.
const PRICE_FEED_MAX_AGE_MS = 60 * 60 * 1000; // 1 hour

const isUsablePrice = (n: unknown): n is number =>
  typeof n === 'number' && Number.isFinite(n) && n > 0;

/**
 * Individual price sources, ordered by reliability. Each returns a live BCH/USD
 * spot price or null. The first two (Coinbase, Kraken) are CORS-enabled public
 * exchange APIs the browser can call DIRECTLY — no proxy needed — which makes
 * them fast and dependable. The last (bchexplorer via the CORS proxy) is a
 * best-effort fallback that can time out, so it must never be the only source.
 */

// Coinbase spot price: { data: { amount: "226.00", base, currency } }.
async function fromCoinbase(signal?: AbortSignal): Promise<number | null> {
  try {
    const res = await fetch('https://api.coinbase.com/v2/prices/BCH-USD/spot', {
      signal,
      headers: { accept: 'application/json' },
    });
    if (!res.ok) return null;
    const json = (await res.json()) as { data?: { amount?: string } };
    const price = parseFloat(json?.data?.amount ?? '');
    return isUsablePrice(price) ? price : null;
  } catch {
    return null;
  }
}

// Kraken ticker: result.BCHUSD.c[0] is the last-trade price.
async function fromKraken(signal?: AbortSignal): Promise<number | null> {
  try {
    const res = await fetch(
      'https://api.kraken.com/0/public/Ticker?pair=BCHUSD',
      { signal, headers: { accept: 'application/json' } },
    );
    if (!res.ok) return null;
    const json = (await res.json()) as {
      result?: Record<string, { c?: string[] }>;
    };
    const pair = json?.result && Object.values(json.result)[0];
    const price = parseFloat(pair?.c?.[0] ?? '');
    return isUsablePrice(price) ? price : null;
  } catch {
    return null;
  }
}

// bchexplorer.cash rates feed (via CORS proxy). Best-effort fallback only.
async function fromBchExplorer(signal?: AbortSignal): Promise<number | null> {
  try {
    const res = await fetch(
      CORS('https://bchexplorer.cash/api/v1/prices'),
      { signal },
    );
    if (!res.ok) return null;
    const json = (await res.json()) as { time?: number; USD?: number };
    const price = json.USD;
    if (!isUsablePrice(price)) return null;
    if (typeof json.time === 'number') {
      const ageMs = Date.now() - json.time * 1000;
      if (ageMs > PRICE_FEED_MAX_AGE_MS) return null;
    }
    return price;
  } catch {
    return null;
  }
}

/**
 * Fetch a live, real BCH/USD spot price. Tries reliable CORS-enabled exchange
 * APIs first, then a proxied explorer feed. Returns null only if every source
 * fails — so a payment amount is never derived from an untrustworthy price, but
 * a single flaky endpoint no longer blocks checkout.
 */
async function fetchLiveBchUsd(signal?: AbortSignal): Promise<number | null> {
  for (const source of [fromCoinbase, fromKraken, fromBchExplorer]) {
    const price = await source(signal);
    if (signal?.aborted) return null;
    if (isUsablePrice(price)) return price;
  }
  return null;
}

type VerifyState = 'idle' | 'checking' | 'notfound' | 'paid' | 'error';

interface VerifyResponse {
  paid: boolean;
  txid?: string;
  confirmations?: number;
  reason?: string;
}

export function BchPaymentDialog({
  open,
  onOpenChange,
  onPaid,
  plan = 'monthly',
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /**
   * Called once a matching payment is detected on-chain. Receives the exact
   * amount and the checkout start time so the parent can record the
   * entitlement server-side (which re-verifies the payment authoritatively).
   */
  onPaid: (payment: {
    amount: string;
    since: number;
  }) => void | Promise<{ license?: string } | void>;
  plan?: BillingPlan;
}) {
  const priceUsd = planPriceUsd(plan);
  const billingLabel = plan === 'yearly' ? 'year' : 'month';
  const durationLabel = plan === 'yearly' ? '12 months' : '30 days';
  const [copied, setCopied] = useState(false);
  const [licenseCopied, setLicenseCopied] = useState(false);
  const [issuedLicense, setIssuedLicense] = useState<string | null>(null);
  const [state, setState] = useState<VerifyState>('idle');
  const [message, setMessage] = useState('');
  const { data } = useLiquidityData();

  // Live, trustworthy BCH/USD price state. We refuse to quote an amount unless
  // we have a genuinely fresh, real price — never a stale or baseline value.
  const [priceState, setPriceState] = useState<'loading' | 'ready' | 'error'>(
    'loading',
  );

  // The active quote: a per-session amount, the price it was derived from, and
  // when it was created. The amount = base + a small random offset so the
  // payment can be matched on-chain. The amount is FROZEN the moment it is first
  // shown (see `frozenRef`) so it can NEVER shift under a user who is scanning
  // or paying. Scan -> pay -> done: no button, no moving target.
  interface Quote {
    amount: string;
    bchUsd: number;
    quotedAt: number;
    startedAt: number;
    plan: BillingPlan;
  }
  const [quote, setQuote] = useState<Quote | null>(null);
  const [nowTick, setNowTick] = useState(() => Date.now());
  // True once an amount has been shown to the user. From that instant the quote
  // is locked; the only way to get a new amount is an explicit "refresh price"
  // action when the quote has gone stale AND no payment has arrived.
  const frozenRef = useRef(false);

  // A collected observation is only usable if it is FRESH and recent — the
  // reference baseline (quality 'stale') must never be used to bill a user.
  const collectedBch = data?.snapshot.observations.find(
    (o) => o.indicatorId === 'bch_usd',
  );
  const collectedUsable =
    collectedBch &&
    collectedBch.quality === 'fresh' &&
    typeof collectedBch.value === 'number' &&
    collectedBch.value > 0 &&
    Date.now() - collectedBch.timestamp <= PRICE_MAX_AGE_MS
      ? collectedBch.value
      : null;

  // Resolve a real price and build a fresh quote. Preserves the on-chain
  // `startedAt` window across refreshes so verification keeps working, but
  // re-derives the amount from the newest price. No-op once frozen.
  const buildQuote = async (signal?: AbortSignal): Promise<void> => {
    if (frozenRef.current) return;
    setPriceState((s) => (quote ? s : 'loading'));
    const live = await fetchLiveBchUsd(signal);
    if (signal?.aborted) return;
    const price = live ?? collectedUsable ?? null;
    if (typeof price !== 'number' || price <= 0) {
      if (!quote) setPriceState('error');
      return;
    }
    // Cross-check the two independent price sources before charging anyone.
    // If the live feed and the app's own CoinGecko price disagree by more than
    // 10%, one of them is glitching — refuse to quote rather than risk over-
    // or under-charging. (When only one source is available we trust its
    // freshness/positivity guards.)
    if (
      typeof live === 'number' &&
      typeof collectedUsable === 'number' &&
      collectedUsable > 0
    ) {
      const disagreement = Math.abs(live - collectedUsable) / collectedUsable;
      if (disagreement > 0.1) {
        if (!quote) setPriceState('error');
        return;
      }
    }
    const base = priceUsd / price;
    const offset = Math.floor(1000 + Math.random() * 8000) / 1e8; // 0.00001–0.00009
    setQuote((prev) => ({
      amount: (base + offset).toFixed(8),
      bchUsd: price,
      quotedAt: Date.now(),
      // Keep the original session start (widens the accepted on-chain window)
      // unless this is the first quote for the session.
      startedAt: prev && prev.plan === plan ? prev.startedAt : Date.now(),
      plan,
    }));
    setPriceState('ready');
    // Lock the amount the instant it is shown. The user may scan/pay at any
    // moment, so the amount must never change out from under them.
    frozenRef.current = true;
  };

  // On open (or plan change while open), build the initial quote.
  useEffect(() => {
    if (!open) return;
    frozenRef.current = false;
    setQuote(null);
    setPriceState('loading');
    const ac = new AbortController();
    buildQuote(ac.signal);
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, plan]);

  // Tick a clock so the "quoted N seconds ago" label stays live. We do NOT
  // silently re-quote: the amount is frozen once shown so an in-flight payment
  // can never be invalidated by a changing amount. If the quote goes stale the
  // user is offered an explicit "refresh price" action instead.
  useEffect(() => {
    if (!open) return;
    const clock = setInterval(() => setNowTick(Date.now()), 1000);
    return () => {
      clearInterval(clock);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, state]);

  const amountBch = quote?.amount ?? null;
  const bchUsd = quote?.bchUsd ?? null;
  const startedAt = quote?.startedAt ?? Date.now();
  const uri = amountBch ? `${BCH_ADDRESS}?amount=${amountBch}` : '';

  const quoteAgeMs = quote ? nowTick - quote.quotedAt : 0;
  const quoteStale = quote ? quoteAgeMs > QUOTE_MAX_AGE_MS : false;

  // Reset everything whenever the dialog closes.
  useEffect(() => {
    if (!open) {
      setQuote(null);
      frozenRef.current = false;
      setState('idle');
      setMessage('');
      setIssuedLicense(null);
      setLicenseCopied(false);
    }
  }, [open]);

  const verify = async (): Promise<boolean> => {
    if (!amountBch) return false;
    setState('checking');
    setMessage('');
    try {
      const res = await fetch(
        `/.netlify/functions/verify-payment?amount=${amountBch}&since=${startedAt}`,
      );

      // Try to parse a JSON body regardless of status — the verifier returns a
      // structured { paid, reason } even on 503 (explorer temporarily busy).
      let data: VerifyResponse | null = null;
      try {
        data = (await res.json()) as VerifyResponse;
      } catch {
        data = null;
      }

      if (data?.paid) {
        setState('paid');
        return true;
      }

      // A 503 with a reason means the block explorer is momentarily busy — this
      // is transient and the poller will retry, so keep the waiting state calm
      // rather than showing a scary "couldn't reach verifier" error.
      if (data) {
        setState('notfound');
        setMessage(
          data.reason === 'explorer unavailable'
            ? 'Confirming on the BCH network — this can take a moment.'
            : 'No matching payment detected yet. It can take a minute to appear.',
        );
        return false;
      }

      // No parseable body at all — a genuine transport/verifier problem.
      setState('error');
      setMessage('Still connecting to the verifier — retrying automatically.');
      return false;
    } catch {
      setState('error');
      setMessage('Network error while verifying. Retrying automatically.');
      return false;
    }
  };

  // Auto-poll the blockchain from the moment an amount exists until payment is
  // found. Deliberately does NOT depend on transient verify states (checking/
  // notfound/error) so the interval isn't torn down and restarted on every
  // poll — it runs steadily and stops only once paid or the dialog closes.
  // A ref guards against overlapping in-flight checks.
  const pollingRef = useRef(false);
  const paidRef = useRef(false);
  useEffect(() => {
    if (!open || !amountBch) return;
    paidRef.current = false;

    const tick = async () => {
      if (pollingRef.current || paidRef.current) return;
      pollingRef.current = true;
      try {
        const ok = await verify();
        if (ok) {
          paidRef.current = true;
          const result = await onPaid({ amount: amountBch, since: startedAt });
          if (result && 'license' in result && result.license) {
            setIssuedLicense(result.license);
          }
        }
      } finally {
        pollingRef.current = false;
      }
    };

    // Check immediately, then on a fixed cadence.
    tick();
    const id = setInterval(tick, POLL_INTERVAL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, amountBch, startedAt]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(BCH_ADDRESS);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  const copyLicense = async () => {
    if (!issuedLicense) return;
    try {
      await navigator.clipboard.writeText(issuedLicense);
      setLicenseCopied(true);
      setTimeout(() => setLicenseCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  // Explicit, user-initiated re-price when the quote has gone stale and no
  // payment has been detected. Safe because the user is choosing to get a new
  // amount before paying (nothing is in flight).
  const handleRefreshQuote = () => {
    frozenRef.current = false; // buildQuote will re-freeze immediately
    const ac = new AbortController();
    buildQuote(ac.signal);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Pay with Bitcoin Cash</DialogTitle>
          <DialogDescription>
            Send the <span className="font-medium text-foreground">exact</span>{' '}
            amount below to unlock Pro for {durationLabel}. Non-custodial — paid
            directly on-chain and verified automatically.
          </DialogDescription>
        </DialogHeader>

        {state === 'paid' && issuedLicense ? (
          <div className="flex w-full min-w-0 flex-col items-center gap-4 py-2 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/15 text-primary">
              <Check className="h-6 w-6" />
            </div>
            <div>
              <p className="text-base font-semibold text-primary">
                Payment confirmed — Pro unlocked! 🎉
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Pro is now active on this device for {durationLabel}. No account
                needed.
              </p>
            </div>

            <div className="w-full space-y-2 rounded-lg border border-primary/30 bg-primary/5 p-3 text-left">
              <p className="text-xs font-medium">
                Your license key — save it to restore Pro on another device:
              </p>
              <button
                onClick={copyLicense}
                className="flex w-full min-w-0 items-center justify-between gap-2 rounded-md border border-border/60 bg-background p-2.5 text-left font-mono text-xs"
              >
                <span className="min-w-0 flex-1 truncate">{issuedLicense}</span>
                {licenseCopied ? (
                  <Check className="h-4 w-4 shrink-0 text-primary" />
                ) : (
                  <Copy className="h-4 w-4 shrink-0 text-muted-foreground" />
                )}
              </button>
              <p className="text-[11px] text-muted-foreground">
                Anyone with this key has Pro — keep it private. It&apos;s saved
                automatically in this browser.
              </p>
            </div>

            <Button className="w-full" onClick={() => onOpenChange(false)}>
              Done
            </Button>
          </div>
        ) : priceState === 'loading' ? (
          <div className="flex w-full min-w-0 flex-col items-center gap-3 py-10 text-center">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">
              Fetching the live BCH price…
            </p>
          </div>
        ) : priceState === 'error' || !amountBch || bchUsd === null ? (
          <div className="flex w-full min-w-0 flex-col items-center gap-3 py-8 text-center">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/15 text-red-400">
              <AlertCircle className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-medium">Live price unavailable</p>
              <p className="mt-1 text-xs text-muted-foreground">
                We can&apos;t confirm the current BCH price right now, so we
                won&apos;t quote an amount that could over- or under-charge you.
                Please try again in a moment.
              </p>
            </div>
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => onOpenChange(false)}
            >
              Close
            </Button>
          </div>
        ) : (
          <div className="flex w-full min-w-0 flex-col items-center gap-4">
            <div className="rounded-xl bg-white p-3">
              <QRCodeCanvas value={uri} size={196} level="M" />
            </div>

            <div className="text-center">
              <p className="font-mono text-2xl font-bold">{amountBch} BCH</p>
              <p className="text-xs text-muted-foreground">
                ≈ ${priceUsd}.00 / {billingLabel} · BCH @ $
                {bchUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </p>
              <p className="mt-1 flex items-center justify-center gap-1 text-[11px] text-muted-foreground">
                {state === 'paid' ? (
                  <>Amount locked · payment received.</>
                ) : quoteStale ? (
                  <span className="text-orange-400">
                    Price may have drifted — refresh if you haven&apos;t paid yet.
                  </span>
                ) : (
                  <>Amount locked — scan and pay.</>
                )}
              </p>
            </div>

            {quoteStale && state !== 'paid' && (
              <Button
                variant="outline"
                className="w-full gap-2"
                onClick={handleRefreshQuote}
              >
                <RefreshCw className="h-4 w-4" /> Refresh price
              </Button>
            )}

            <button
              onClick={copy}
              className="flex w-full min-w-0 items-center justify-between gap-2 rounded-lg border border-border/60 bg-secondary/40 p-2.5 text-left font-mono text-xs"
            >
              <span className="min-w-0 flex-1 truncate">{BCH_ADDRESS}</span>
              {copied ? (
                <Check className="h-4 w-4 shrink-0 text-primary" />
              ) : (
                <Copy className="h-4 w-4 shrink-0 text-muted-foreground" />
              )}
            </button>

            {state === 'paid' ? (
              <div className="flex w-full items-center justify-center gap-2 rounded-lg border border-primary/30 bg-primary/10 p-2.5 text-sm font-medium text-primary">
                <Check className="h-4 w-4" /> Payment confirmed — unlocking Pro
              </div>
            ) : (
              <div className="flex w-full items-center justify-center gap-2 rounded-lg border border-border/60 bg-secondary/40 p-2.5 text-sm font-medium text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
                Waiting for your payment…
              </div>
            )}

            {state === 'error' && message && (
              <p className="flex items-center justify-center gap-1.5 text-center text-[11px] text-muted-foreground">
                <AlertCircle className="h-3 w-3 shrink-0" /> {message}
              </p>
            )}

            <p className="text-center text-[11px] text-muted-foreground">
              Scan the code and send the exact amount. Pro unlocks automatically
              the moment your payment hits the BCH network — usually within a
              minute. You can keep this screen open or leave it; no button to
              press.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
